/** Space above a sought transcript message, clear of the top fade and resize handle. */
const TRANSCRIPT_SCROLL_TARGET_OFFSET_PX = 48;

/** Keep distant seeks from animating through the entire transcript; a transcript's first scroll animates less. */
const MAX_INITIAL_SMOOTH_SCROLL_DISTANCE_PX = 600;
const MAX_SMOOTH_SCROLL_DISTANCE_PX = 1200;

/** Transcript containers that have scrolled since they were shown. */
const scrolledContainers = new WeakSet<HTMLElement>();

/** A message's first fragment rests with its whole message, so the speaker line above it stays in view; a later fragment rests by itself. */
function findScrollTarget(element: Element): Element {
  const message = element.previousElementSibling
    ? null
    : element.closest("[data-transcript-message]");
  return message ?? element;
}

/** The scroll position that rests an element just below the top of its container, within the scrollable range. */
export function computeIdealScrollTopForElement(
  container: Element,
  element: Element,
): number {
  const maxScrollTop = Math.max(
    0,
    container.scrollHeight - container.clientHeight,
  );
  const elementTop =
    findScrollTarget(element).getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop;

  return Math.max(
    0,
    Math.min(elementTop - TRANSCRIPT_SCROLL_TARGET_OFFSET_PX, maxScrollTop),
  );
}

/**
 * The navigator highlights the element on the reading line. The line sits at the scroll target offset, and over the
 * last pane-height of scrolling slides down to the same offset above the bottom, so elements too near the end to
 * scroll to the top can still be highlighted. It never moves the scroll.
 */
function computeReadingLineTop(container: Element): number {
  const maxScrollTop = Math.max(
    0,
    container.scrollHeight - container.clientHeight,
  );
  const distance = Math.max(
    0,
    container.clientHeight - 2 * TRANSCRIPT_SCROLL_TARGET_OFFSET_PX,
  );
  const start = Math.max(0, maxScrollTop - distance);
  // Line pixels moved per scrolled pixel once the slide starts
  const rate = maxScrollTop > start ? distance / (maxScrollTop - start) : 0;
  return (
    TRANSCRIPT_SCROLL_TARGET_OFFSET_PX +
    Math.max(0, container.scrollTop - start) * rate
  );
}

/** The last of the given elements, in document order, that starts at or above the reading line; else the first. */
export function findElementAtReadingLine(
  container: Element,
  elements: readonly Element[],
): Element | undefined {
  const line =
    container.getBoundingClientRect().top + computeReadingLineTop(container);
  let low = 0;
  let high = elements.length - 1;
  let found = elements[0];
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const element = elements[middle];
    if (!element) break;
    // Measures from where a scroll rests the element, which for a message's first fragment is its speaker line
    if (findScrollTarget(element).getBoundingClientRect().top <= line + 1) {
      found = element;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return found;
}

/**
 * Marks a scroll as system-driven or reader-driven for audio following. Following clears it once the scroll ends.
 */
export const SCROLLING_ROLE_DATA_KEY = "arScrollingRole";

export function scrollTranscriptContainer(
  container: HTMLElement,
  top: number,
  behavior: ScrollBehavior = "smooth",
  role: "system" | "reader" = "system",
): void {
  // Nothing scrolls when the container already rests there, so no scroll end would clear the mark
  if (Math.abs(top - container.scrollTop) < 1) {
    return;
  }

  container.dataset[SCROLLING_ROLE_DATA_KEY] = role;
  const maxSmoothDistance = scrolledContainers.has(container)
    ? MAX_SMOOTH_SCROLL_DISTANCE_PX
    : MAX_INITIAL_SMOOTH_SCROLL_DISTANCE_PX;
  scrolledContainers.add(container);
  const distance = top - container.scrollTop;
  if (behavior === "smooth" && Math.abs(distance) > maxSmoothDistance) {
    container.scrollTo({
      top: top - Math.sign(distance) * maxSmoothDistance,
      behavior: "instant",
    });
  }
  container.scrollTo({ top, behavior });
}

export function scrollToTranscriptElement(
  container: HTMLElement,
  element: Element,
  behavior: ScrollBehavior = "smooth",
  role: "system" | "reader" = "system",
): void {
  const top = computeIdealScrollTopForElement(container, element);
  scrollTranscriptContainer(container, top, behavior, role);
}

export function findClosestScrollContainer(
  element: HTMLElement,
): HTMLElement | null {
  let ancestor: HTMLElement | null = element.parentElement;

  while (ancestor && ancestor.scrollHeight <= ancestor.clientHeight) {
    ancestor = ancestor.parentElement;
  }

  return ancestor;
}
